import React from 'react';
import { formatFinanceCell } from '../utils/taskFinance';
import {
  TaskBalanceColumnKey,
  TaskBalanceRow,
  TaskBalanceTableData,
  TASK_BALANCE_CUMULATIVE_ROW_ID,
  TASK_BALANCE_PERIOD_ROW_ID,
  TASK_EXPENSES_CUMULATIVE_ROW_ID,
  TASK_EXPENSES_PERIOD_ROW_ID,
} from '../utils/taskBalance';

export interface TaskBalanceCellClick {
  rowId: string;
  rowLabel: string;
  columnKey: TaskBalanceColumnKey;
  columnLabel: string;
  value: number;
}

interface TaskBalanceTableProps {
  data: TaskBalanceTableData;
  onCellClick?: (cell: TaskBalanceCellClick) => void;
  showPlanColumns?: boolean;
}

type ExpenseDeltaKind = 'under' | 'over' | 'equal' | 'stub';

function isSignedRow(kind: TaskBalanceRow['kind']): boolean {
  return kind === 'balance' || kind === 'cumulative';
}

function balanceCellClass(value: number, kind: TaskBalanceRow['kind']): string {
  if (!isSignedRow(kind) || value === 0) {
    return '';
  }
  return value > 0
    ? ' task-detail__balance-cell--positive'
    : ' task-detail__balance-cell--negative';
}

function rowClassName(row: TaskBalanceRow): string {
  if (row.kind === 'income') {
    return 'task-detail__balance-row--income';
  }
  if (row.kind === 'expense_total') {
    return 'task-detail__balance-row--expenses-total';
  }
  if (row.kind === 'expense') {
    return 'task-detail__balance-row--expense';
  }
  if (row.id === TASK_BALANCE_PERIOD_ROW_ID || row.id === TASK_EXPENSES_PERIOD_ROW_ID) {
    return 'task-detail__balance-row--period';
  }
  if (row.id === TASK_BALANCE_CUMULATIVE_ROW_ID || row.id === TASK_EXPENSES_CUMULATIVE_ROW_ID) {
    return 'task-detail__balance-row--cumulative';
  }
  return '';
}

function cellExtraClass(row: TaskBalanceRow, columnKey: TaskBalanceColumnKey): string {
  const classes: string[] = [];
  if (columnKey === 'total') {
    classes.push('task-detail__balance-cell--total');
    if (row.id === TASK_BALANCE_CUMULATIVE_ROW_ID || row.id === TASK_EXPENSES_CUMULATIVE_ROW_ID) {
      classes.push('task-detail__balance-cell--cumulative-total');
    }
  }
  return classes.length > 0 ? ` ${classes.join(' ')}` : '';
}

function resolveExpenseDelta(row: TaskBalanceRow): ExpenseDeltaKind {
  if (row.planValue == null) {
    return 'stub';
  }
  const spent = Math.abs(row.values.total ?? 0);
  const plan = row.planValue;
  if (Math.abs(spent - plan) < 0.5) {
    return 'equal';
  }
  return spent < plan ? 'under' : 'over';
}

function renderDeltaIcon(kind: ExpenseDeltaKind): React.ReactNode {
  if (kind === 'stub') {
    return '—';
  }
  if (kind === 'equal') {
    return (
      <span className="task-detail__balance-delta task-detail__balance-delta--equal" title="Соответствует плану">
        =
      </span>
    );
  }
  if (kind === 'under') {
    return (
      <span className="task-detail__balance-delta task-detail__balance-delta--under" title="Меньше плана">
        ▼
      </span>
    );
  }
  return (
    <span className="task-detail__balance-delta task-detail__balance-delta--over" title="Больше плана">
      ▲
    </span>
  );
}

const TaskBalanceTable: React.FC<TaskBalanceTableProps> = ({ data, onCellClick, showPlanColumns }) => {
  const isExpenses = data.variant === 'expenses';
  const showPlan = isExpenses && Boolean(showPlanColumns);
  const periodColumnCount = data.columns.filter((column) => column.key !== 'total').length;

  const renderCell = (row: TaskBalanceRow, columnKey: TaskBalanceColumnKey, columnLabel: string): React.ReactNode => {
    const value = row.values[columnKey] ?? 0;
    const signed = !isExpenses && isSignedRow(row.kind);
    const isEmpty = value === 0 && !signed;
    const display = isEmpty ? '—' : formatFinanceCell(value);
    const clickable = Boolean(onCellClick) && !isEmpty;
    return (
      <td
        key={`${row.id}-${columnKey}`}
        className={`task-detail__balance-cell${signed ? balanceCellClass(value, row.kind) : ''}${cellExtraClass(row, columnKey)}${
          clickable ? ' task-detail__balance-cell--clickable' : ''
        }`}
        onClick={
          clickable
            ? () =>
                onCellClick?.({
                  rowId: row.id,
                  rowLabel: row.label,
                  columnKey,
                  columnLabel,
                  value,
                })
            : undefined
        }
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={
          clickable
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onCellClick?.({
                    rowId: row.id,
                    rowLabel: row.label,
                    columnKey,
                    columnLabel,
                    value,
                  });
                }
              }
            : undefined
        }
      >
        {display}
      </td>
    );
  };

  return (
    <div className="task-detail__balance-wrap">
      <table
        className={`task-detail__mini-table task-detail__mini-table--compact task-detail__balance-table${
          isExpenses ? ' task-detail__balance-table--expenses' : ''
        }${showPlan ? ' task-detail__balance-table--expenses-plan' : ''}`}
      >
        <colgroup>
          <col className="task-detail__balance-col-label" />
          {showPlan ? <col className="task-detail__balance-col-delta" /> : null}
          {showPlan ? <col className="task-detail__balance-col-plan" /> : null}
          <col className="task-detail__balance-col-total" />
          {Array.from({ length: periodColumnCount }, (_, index) => (
            <col key={`period-${index}`} className="task-detail__balance-col-period" />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="task-detail__balance-label-head" />
            {showPlan ? (
              <th className="task-detail__balance-col-head task-detail__balance-col-head--delta" title="Дельта">
                Δ
              </th>
            ) : null}
            {showPlan ? (
              <th className="task-detail__balance-col-head task-detail__balance-col-head--plan">План</th>
            ) : null}
            {data.columns.map((column) => (
              <th
                key={column.key}
                className={`task-detail__balance-col-head${
                  column.key === 'total' ? ' task-detail__balance-col-head--total' : ''
                }`}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr key={row.id} className={rowClassName(row)}>
              <th scope="row" className="task-detail__balance-row-label">
                {row.label}
              </th>
              {showPlan ? (
                <td className="task-detail__balance-cell task-detail__balance-cell--delta">
                  {renderDeltaIcon(resolveExpenseDelta(row))}
                </td>
              ) : null}
              {showPlan ? (
                <td className="task-detail__balance-cell task-detail__balance-cell--plan">
                  {formatFinanceCell(row.planValue ?? null)}
                </td>
              ) : null}
              {data.columns.map((column) => renderCell(row, column.key, column.label))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TaskBalanceTable;
